/**
 * 被踢下线的**原因**→ 登录页横幅文案(修复波 0926 第十一批 B2 #6)。
 *
 * ★为什么:此前 401 SESSION_KICKED 一码多因(被别处登录挤掉 / 账号被禁用 / 公司被停用 /
 *   管理员重置了密码 / 组织关系变更),登录页统一说「账号已在别处登录」—— 员工被禁用后
 *   看到这句会去改密码、找 IT 查异地登录,方向全错。
 * ★api 侧(B1 同批)在 SESSION_KICKED 响应里加 `reason`;httpClient 把它存进
 *   sessionStorage `xbgeo-session-kicked-reason`(与既有 `xbgeo-session-kicked` 同范式),
 *   这里按它分句。**没有 reason 时用中性句**「登录已失效,请重新登录」—— 不再默认猜「别处登录」。
 * ★reason 词面宽容归一(大小写 / 同义写法),认不出的一律当作没有 reason,不编原因。
 */

/** key 与 `shared/api/httpClient.ts` 的 KICKED_REASON_KEY 同一个字面量(shared 层不反向 import feature)。 */
export const KICKED_REASON_KEY = "xbgeo-session-kicked-reason";

/**
 * ★B1(api fix/fw0926i-tenant)契约:SESSION_KICKED 响应体可选 `reason` ∈
 *   USER_DISABLED / USER_DELETED / PASSWORD_RESET / WECOM_UNBOUND / WECOM_LEFT /
 *   TENANT_SUSPENDED / TENANT_DELETED / ROLE_CHANGED;**无 reason = 别处登录或登出** → 中性句。
 */
export type KickedReason =
  | "account_disabled"
  | "account_deleted"
  | "tenant_suspended"
  | "tenant_deleted"
  | "password_reset"
  | "wecom_unbound"
  | "wecom_left"
  | "role_changed"
  | "logged_in_elsewhere";

const ALIASES: Record<string, KickedReason> = {
  user_disabled: "account_disabled",
  account_disabled: "account_disabled",
  user_deleted: "account_deleted",
  account_deleted: "account_deleted",
  tenant_suspended: "tenant_suspended",
  tenant_disabled: "tenant_suspended",
  tenant_deleted: "tenant_deleted",
  password_reset: "password_reset",
  password_changed: "password_reset",
  wecom_unbound: "wecom_unbound",
  wecom_left: "wecom_left",
  role_changed: "role_changed",
  membership_changed: "role_changed",
  org_changed: "role_changed",
  logged_in_elsewhere: "logged_in_elsewhere",
  elsewhere: "logged_in_elsewhere",
  concurrent_login: "logged_in_elsewhere",
};

export function normalizeKickedReason(raw: unknown): KickedReason | null {
  if (typeof raw !== "string") return null;
  const key = raw
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  return ALIASES[key] ?? null;
}

/** i18n key(user.json `login.*`)。null = 无 reason → 中性句。 */
export function kickedMessageKey(reason: KickedReason | null): string {
  switch (reason) {
    case "account_disabled":
      return "login.kickedAccountDisabled";
    case "account_deleted":
      return "login.kickedAccountDeleted";
    case "tenant_suspended":
      return "login.kickedTenantSuspended";
    case "tenant_deleted":
      return "login.kickedTenantDeleted";
    case "password_reset":
      return "login.kickedPasswordReset";
    case "wecom_unbound":
      return "login.kickedWecomUnbound";
    case "wecom_left":
      return "login.kickedWecomLeft";
    case "role_changed":
      return "login.kickedMembershipChanged";
    case "logged_in_elsewhere":
      return "login.kickedMessage";
    default:
      return "login.kickedGeneric";
  }
}
